import { useEffect, useState } from 'react';
import { MobileHeader, HeaderAction } from '../components/MobileHeader';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock, ChevronDown, ChevronUp, MapPin, CheckCircle, XCircle, Calendar, Plus, Share2 } from 'lucide-react';
import { useLang } from '../i18n';
import { api } from '../api';
import { Avatar } from './Dashboard';
import { cn } from '../lib/utils';
import PrivateClassModal from '../components/PrivateClassModal';
import PrivateCardSheet from '../components/privates/PrivateCardSheet';

export default function PrivateSessions() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const locale = lang === 'fr' ? fr : undefined;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [showPrivateModal, setShowPrivateModal] = useState(false);
  const [showCard, setShowCard] = useState(false);  // v2.30 : grille des tarifs à partager
  const [allStudents, setAllStudents] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get('/private-overview').catch(() => []).then(d => { setData(d); setLoading(false); });
    api.get('/students').then(setAllStudents).catch(() => {});
    api.get('/locations').then(setLocations).catch(() => {});
  }, []);

  const filtered = data.filter(d => {
    if (filter === 'has_remaining') return d.hours_remaining > 0;
    if (filter === 'completed') return d.hours_remaining <= 0 && d.hours_done > 0;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div>;

  return (
    <div className="max-w-5xl mx-auto" data-testid="private-sessions-page">
      <MobileHeader title="Privés"
        actions={<>
          <HeaderAction icon={Share2} label="Partager mes tarifs" onClick={() => setShowCard(true)} testId="share-private-prices-btn" />
          <HeaderAction icon={Plus} label="Nouvelle séance" primary onClick={() => setShowPrivateModal(true)} testId="add-private-session-btn-mobile" />
        </>} />
      {showCard && <PrivateCardSheet onClose={() => setShowCard(false)} />}

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-[#26A69A] font-manrope">
            {data.reduce((s, d) => s + d.hours_done, 0).toFixed(1)}h
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{t('privateSessions.done')}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-[#0288D1] font-manrope">
            {data.reduce((s, d) => s + d.hours_scheduled, 0).toFixed(1)}h
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{t('privateSessions.scheduled')}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-amber-500 font-manrope">
            {data.reduce((s, d) => s + d.hours_remaining, 0).toFixed(1)}h
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{t('privateSessions.toSchedule')}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {[['all', 'Tous'], ['has_remaining', 'Heures à planifier'], ['completed', 'Complétés']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            className={cn('px-3 py-1.5 rounded-xl text-sm font-semibold transition-colors',
              filter === val ? 'bg-[#0288D1] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
            data-testid={`private-filter-${val}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Student cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100 text-slate-400 text-sm">
          <Clock size={40} className="mx-auto mb-3 opacity-30" />
          {t('privateSessions.noPrivates')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => (
            <PrivateCard
              key={d.student.id}
              d={d}
              locale={locale}
              t={t}
              expanded={expanded === d.student.id}
              onToggle={() => setExpanded(p => p === d.student.id ? null : d.student.id)}
              onStudentClick={() => navigate(`/students/${d.student.id}`)}
            />
          ))}
        </div>
      )}
      {showPrivateModal && (
        <PrivateClassModal
          locations={locations}
          allStudents={allStudents}
          onClose={() => setShowPrivateModal(false)}
          onSaved={() => {
            setShowPrivateModal(false);
            api.get('/private-overview').catch(() => []).then(setData);
          }}
        />
      )}
    </div>
  );
}

function PrivateCard({ d, locale, t, expanded, onToggle, onStudentClick }) {
  const { student, total_hours, hours_done, hours_scheduled, hours_remaining, sessions } = d;
  const donePct = Math.min(100, (hours_done / total_hours) * 100);
  const scheduledPct = Math.min(100 - donePct, (hours_scheduled / total_hours) * 100);
  const remainPct = Math.max(0, 100 - donePct - scheduledPct);

  const pastSessions = sessions.filter(s => s.is_past);
  const futureSessions = sessions.filter(s => !s.is_past);

  const fmtDate = (iso) => {
    try { return format(parseISO(iso), 'd MMM yyyy · HH:mm', { locale }); } catch { return iso; }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="private-card">
      <div className="p-4">
        {/* Top: student info + stats */}
        <div className="flex items-center justify-between mb-3">
          <div
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onStudentClick}
          >
            <Avatar name={`${student.first_name} ${student.last_name}`} size="lg" />
            <div>
              <div className="font-bold text-slate-900 font-manrope">{student.first_name} {student.last_name}</div>
              {d.packages?.[0] && (
                <div className="text-xs text-slate-500">
                  {d.packages[0].total_hours}h · {d.packages[0].total_price} CHF
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold text-slate-900 font-manrope">{total_hours}h</div>
            <div className="text-xs text-slate-400">total</div>
          </div>
        </div>

        {/* 3-part progress bar */}
        <div className="flex h-3 rounded-full overflow-hidden gap-0.5 mb-2">
          {donePct > 0 && (
            <div className="bg-[#26A69A] rounded-full transition-all" style={{ width: `${donePct}%` }} title={`${hours_done}h effectuées`} />
          )}
          {scheduledPct > 0 && (
            <div className="bg-[#0288D1] rounded-full transition-all" style={{ width: `${scheduledPct}%` }} title={`${hours_scheduled}h planifiées`} />
          )}
          {remainPct > 0 && (
            <div className="bg-slate-200 flex-1 rounded-full" title={`${hours_remaining}h à planifier`} />
          )}
          {donePct === 0 && scheduledPct === 0 && (
            <div className="bg-slate-200 w-full rounded-full" />
          )}
        </div>

        {/* Legend */}
        <div className="flex gap-4 text-xs mb-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#26A69A]" />
            <span className="text-slate-600">{hours_done}h <span className="text-slate-400">{t('privateSessions.done').toLowerCase()}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#0288D1]" />
            <span className="text-slate-600">{hours_scheduled}h <span className="text-slate-400">{t('privateSessions.scheduled').toLowerCase()}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
            <span className={cn('font-semibold', hours_remaining > 0 ? 'text-amber-600' : 'text-slate-400')}>
              {hours_remaining}h <span className="font-normal text-slate-400">{t('privateSessions.toSchedule').toLowerCase()}</span>
            </span>
          </div>
        </div>

        {/* Expand button */}
        {sessions.length > 0 && (
          <button onClick={onToggle}
            className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#0288D1] transition-colors py-1"
            data-testid={`expand-sessions-${student.id}`}>
            {expanded ? <><ChevronUp size={14} /> {t('privateSessions.collapse')}</> : <><ChevronDown size={14} /> {t('privateSessions.expand')} ({sessions.length})</>}
          </button>
        )}
      </div>

      {/* Expanded sessions */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-4">
          {futureSessions.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('privateSessions.future')}</div>
              <div className="space-y-2">
                {futureSessions.map(s => (
                  <SessionRow key={s.id} session={s} fmtDate={fmtDate} future />
                ))}
              </div>
            </div>
          )}
          {pastSessions.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('privateSessions.past')}</div>
              <div className="space-y-2">
                {pastSessions.map(s => (
                  <SessionRow key={s.id} session={s} fmtDate={fmtDate} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, fmtDate, future }) {
  return (
    <div className={cn(
      'bg-white rounded-xl px-3 py-2.5 border flex items-center justify-between gap-2',
      future ? 'border-[#0288D1]/20' : 'border-slate-100'
    )} data-testid="private-session-row">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Calendar size={13} className={future ? 'text-[#0288D1]' : 'text-slate-400'} />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 truncate capitalize">{fmtDate(session.datetime_start)}</div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Clock size={11} />
            {session.dur_hours}h
            {session.location?.venue_name && (
              <>
                <MapPin size={11} />
                {session.location.venue_name}
              </>
            )}
          </div>
          {session.content && (
            <div className="text-xs text-slate-500 italic mt-0.5 truncate">"{session.content}"</div>
          )}
        </div>
      </div>
      {!future && (
        session.present
          ? <CheckCircle size={18} className="text-[#26A69A] shrink-0" />
          : <XCircle size={18} className="text-red-400 shrink-0" />
      )}
      {future && <span className="text-xs font-bold text-[#0288D1] shrink-0">Planifié</span>}
    </div>
  );
}
